import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import PageLayout from '../components/layout/PageLayout';
import InstituteCreateForm from '../components/institutions/InstituteCreateForm';
import instituteService from '../services/instituteService';
import { useAuth } from '../contexts/AuthContext';
import { canManageInstitute } from '../utils/institutePermissions';

const InstituteEditPage = () => {
  const { id: instituteId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [initialInstitute, setInitialInstitute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    const loadInstitute = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const data = await instituteService.getInstitute(instituteId);
        if (user && !canManageInstitute(user, data)) {
          setLoadError('You do not have permission to edit this institute.');
          return;
        }
        setInitialInstitute(data);
      } catch (err) {
        console.error('Error loading institute for edit:', err);
        setLoadError('Failed to load institute');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated && instituteId) {
      loadInstitute();
    } else if (!isAuthenticated) {
      setLoading(false);
      setLoadError('Please log in to edit this institute.');
    }
  }, [instituteId, isAuthenticated, user]);

  if (loading) {
    return (
      <PageLayout title="Edit Institute - PakPoints">
        <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
            <p className="mt-4 text-gray-600">Loading institute...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (loadError || !initialInstitute) {
    return (
      <PageLayout title="Edit Institute - PakPoints">
        <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600">{loadError || 'Institute not found'}</p>
            <button
              type="button"
              onClick={() => navigate(instituteId ? `/institutes/${instituteId}` : '/institutes')}
              className="mt-4 text-blue-600 hover:underline"
            >
              Back
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Edit Institute - PakPoints">
      <Helmet>
        <title>Edit Institute - PakPoints</title>
        <meta name="description" content="Edit educational institute details" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <InstituteCreateForm
            isEditMode
            instituteId={instituteId}
            initialInstitute={initialInstitute}
          />
        </div>
      </div>
    </PageLayout>
  );
};

export default InstituteEditPage;
